import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { appendLiveLocationToPath } from "../../utils/fleetMapPath";
import { splitRouteSegments } from "../../utils/routeSegments";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID, GOOGLE_MAPS_VERSION } from "../../utils/googleMapsLoader";

export type FleetConsoleVehicleMap = {
  id: string;
  plate: string;
  name: string;
  status: "trip" | "idle" | "maint";
  liveLocation: { lat: number; lng: number } | null;
  routePoints: { lat: number; lng: number; gapBefore?: boolean; estimated?: boolean }[];
  restLocation: { lat: number; lng: number } | null;
};

type FleetConsoleMapProps = {
  vehicles: FleetConsoleVehicleMap[];
  orgSubtitle: string;
  height?: number;
  /** Hide fleet tabs; show a static route polyline from the first vehicle's routePoints. */
  simpleRoute?: boolean;
};

const defaultCenter = { lat: -1.286389, lng: 36.817223 };

function resolveMapsApiKey(): string {
  return (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "").trim();
}

export function FleetConsoleMap({ vehicles, orgSubtitle, height = 360, simpleRoute = false }: FleetConsoleMapProps) {
  const apiKey = resolveMapsApiKey();
  if (!apiKey) {
    return (
      <div
        style={{
          borderRadius: 12,
          border: "1px solid #F3D9A4",
          background: "#FBEFD9",
          padding: 16,
          fontSize: 13,
          color: "#9A5B00",
        }}
      >
        <strong>Fleet map</strong>
        <p style={{ margin: "8px 0 0" }}>
          Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to{" "}
          <code>pear-admin-dashboard/.env</code> and restart <code>npm start</code>.
        </p>
      </div>
    );
  }

  return (
    <FleetConsoleMapLoaded
      vehicles={vehicles}
      orgSubtitle={orgSubtitle}
      apiKey={apiKey}
      height={height}
      simpleRoute={simpleRoute}
    />
  );
}

function FleetConsoleMapLoaded({
  vehicles,
  orgSubtitle,
  apiKey,
  height = 360,
  simpleRoute = false,
}: FleetConsoleMapProps & { apiKey: string }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
    version: GOOGLE_MAPS_VERSION,
  });
  const mapRef = useRef<google.maps.Map | null>(null);

  const liveVehicles = useMemo(
    () => vehicles.filter((v) => v.status === "trip"),
    [vehicles]
  );
  const [tab, setTab] = useState<"live" | "rest">("rest");
  const [selectedLiveId, setSelectedLiveId] = useState<string | null>(null);

  useEffect(() => {
    if (liveVehicles.length > 0) {
      setTab("live");
      setSelectedLiveId((prev) =>
        prev && liveVehicles.some((v) => v.id === prev) ? prev : liveVehicles[0].id
      );
    } else {
      setTab("rest");
      setSelectedLiveId(null);
    }
  }, [liveVehicles]);

  const selectedLive = liveVehicles.find((v) => v.id === selectedLiveId) ?? liveVehicles[0] ?? null;

  const restMarkers = useMemo(
    () =>
      vehicles
        .map((v) => {
          const pos = v.restLocation ?? (v.status !== "trip" ? v.liveLocation : null);
          if (!pos) return null;
          return { id: v.id, plate: v.plate, pos };
        })
        .filter(Boolean) as { id: string; plate: string; pos: { lat: number; lng: number } }[],
    [vehicles]
  );

  const staticRoutePath = useMemo(() => {
    if (!simpleRoute || !vehicles[0]) return [];
    return (vehicles[0].routePoints ?? []).filter((p) => !Number.isNaN(p.lat) && !Number.isNaN(p.lng));
  }, [simpleRoute, vehicles]);

  const livePath = useMemo(() => {
    if (simpleRoute) return staticRoutePath;
    if (!selectedLive) return [];
    return appendLiveLocationToPath(selectedLive.routePoints ?? [], selectedLive.liveLocation);
  }, [simpleRoute, staticRoutePath, selectedLive]);

  const center = useMemo(() => {
    if (simpleRoute && staticRoutePath.length) return staticRoutePath[0];
    if (tab === "live" && selectedLive) {
      const pos = selectedLive.liveLocation ?? livePath[livePath.length - 1] ?? livePath[0];
      if (pos) return pos;
    }
    if (tab === "rest" && restMarkers.length) return restMarkers[0].pos;
    const anyLive = vehicles.find((v) => v.liveLocation);
    if (anyLive?.liveLocation) return anyLive.liveLocation;
    return defaultCenter;
  }, [simpleRoute, staticRoutePath, tab, selectedLive, livePath, restMarkers, vehicles]);

  const fitMap = useCallback(() => {
    const map = mapRef.current;
    if (!map || typeof google === "undefined") return;
    const bounds = new google.maps.LatLngBounds();
    if (simpleRoute) {
      staticRoutePath.forEach((p) => bounds.extend(p));
    } else if (tab === "live" && selectedLive) {
      livePath.forEach((p) => bounds.extend(p));
      if (selectedLive.liveLocation) bounds.extend(selectedLive.liveLocation);
    } else {
      restMarkers.forEach((m) => bounds.extend(m.pos));
    }
    if (!bounds.isEmpty()) map.fitBounds(bounds, 48);
    else map.setCenter(center);
  }, [simpleRoute, staticRoutePath, tab, selectedLive, livePath, restMarkers, center]);

  useEffect(() => {
    fitMap();
  }, [fitMap, tab, selectedLiveId]);

  if (loadError) {
    return (
      <div style={{ padding: 16, color: "#B91C1C", fontSize: 13 }}>
        Could not load Google Maps ({loadError.message}). Check API key restrictions for localhost.
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#FAF9F6",
          borderRadius: 12,
          color: "#5D646B",
          fontSize: 13,
        }}
      >
        Loading map…
      </div>
    );
  }

  return (
    <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid #E4E3DE" }}>
      {!simpleRoute && (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          padding: "10px 14px",
          background: "#fff",
          borderBottom: "1px solid #EFEDE7",
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0F1419" }}>Fleet map</div>
          <div style={{ fontSize: 11, color: "#5D646B" }}>{orgSubtitle}</div>
        </div>
        <div style={{ display: "inline-flex", borderRadius: 20, background: "#F6F5F1", padding: 3 }}>
          <button
            type="button"
            onClick={() => setTab("live")}
            disabled={liveVehicles.length === 0}
            style={{
              border: "none",
              borderRadius: 18,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: liveVehicles.length ? "pointer" : "not-allowed",
              background: tab === "live" ? "#00707F" : "transparent",
              color: tab === "live" ? "#fff" : "#5D646B",
              fontFamily: "inherit",
            }}
          >
            Live{liveVehicles.length ? ` (${liveVehicles.length})` : ""}
          </button>
          <button
            type="button"
            onClick={() => setTab("rest")}
            style={{
              border: "none",
              borderRadius: 18,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              background: tab === "rest" ? "#00707F" : "transparent",
              color: tab === "rest" ? "#fff" : "#5D646B",
              fontFamily: "inherit",
            }}
          >
            Rest location
          </button>
        </div>
      </div>
      )}

      {!simpleRoute && tab === "live" && liveVehicles.length > 1 && (
        <div style={{ padding: "8px 14px", background: "#FAF9F6", borderBottom: "1px solid #EFEDE7" }}>
          <select
            value={selectedLiveId ?? ""}
            onChange={(e) => setSelectedLiveId(e.target.value)}
            style={{
              borderRadius: 10,
              border: "1px solid #E4E3DE",
              padding: "8px 10px",
              fontSize: 13,
              fontFamily: "inherit",
              maxWidth: "100%",
            }}
          >
            {liveVehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate} · live
              </option>
            ))}
          </select>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={{ width: "100%", height }}
        center={center}
        zoom={13}
        onLoad={(map) => {
          mapRef.current = map;
          fitMap();
        }}
        options={{
          disableDefaultUI: false,
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        {(simpleRoute || tab === "live") &&
          splitRouteSegments(livePath).map((seg, i) =>
            seg.length < 2 ? null : seg[0]?.estimated ? (
              // Estimated (Batch-3) segment: render as a dotted trail so it reads as inferred.
              <Polyline
                key={`est-${i}`}
                path={seg}
                options={{
                  strokeColor: "#00707F",
                  strokeOpacity: 0,
                  icons: [
                    {
                      icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: "#00707F",
                        fillOpacity: 0.9,
                        strokeColor: "#00707F",
                        strokeOpacity: 0.9,
                        scale: 2.4,
                      },
                      offset: "0",
                      repeat: "12px",
                    },
                  ],
                }}
              />
            ) : (
              // One polyline per gap-free segment so an app-kill resume never draws
              // a straight line across the closed-app gap.
              <Polyline key={`route-${i}`} path={seg} options={{ strokeColor: "#00707F", strokeWeight: 4, strokeOpacity: 0.9 }} />
            )
          )}
        {!simpleRoute && tab === "live" && selectedLive?.liveLocation && (
          <Marker
            position={selectedLive.liveLocation}
            title={`${selectedLive.plate} · live`}
            icon={{
              url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
              scaledSize: new google.maps.Size(40, 40),
            }}
          />
        )}
        {!simpleRoute && tab === "rest" &&
          restMarkers.map((m) => (
            <Marker
              key={m.id}
              position={m.pos}
              title={`${m.plate} · last seen`}
              icon={{
                url: "https://maps.google.com/mapfiles/ms/icons/orange-dot.png",
                scaledSize: new google.maps.Size(40, 40),
              }}
            />
          ))}
      </GoogleMap>

      {!simpleRoute && tab === "rest" && restMarkers.length === 0 && (
        <div style={{ padding: "10px 14px", fontSize: 12, color: "#5D646B", background: "#FAF9F6" }}>
          No rest location yet — ends when a trip completes with GPS, or when the vehicle reports live location.
        </div>
      )}
    </div>
  );
}
